import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star,
  Calendar,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Trophy,
  Flame,
  Target,
  Layers,
  Play,
  Sparkles,
  RotateCcw,
  Compass,
  type LucideIcon,
} from 'lucide-react';
import { Particles } from '@/components/ui/particles';
import { useKiprData } from '@/lib/kiprData';
import { isLearnerKipPlayable } from '@/lib/catalogAvailability';
import type { Kip } from '@/types';

type CategoryName = Kip['category'];

interface CategoryTheme {
  stripe: string;
  chip: string;
  ring: string;
  iconBg: string;
  soft: string;
}

const CATEGORY_THEME: Record<CategoryName, CategoryTheme> = {
  Communication: {
    stripe: 'from-blue-400 to-blue-600',
    chip: 'bg-blue-100 text-blue-700',
    ring: 'text-blue-500',
    iconBg: 'from-blue-500 to-blue-600',
    soft: 'bg-blue-50',
  },
  Empathy: {
    stripe: 'from-purple-400 to-purple-600',
    chip: 'bg-purple-100 text-purple-700',
    ring: 'text-purple-500',
    iconBg: 'from-purple-500 to-purple-600',
    soft: 'bg-purple-50',
  },
  Accuracy: {
    stripe: 'from-emerald-400 to-emerald-600',
    chip: 'bg-emerald-100 text-emerald-700',
    ring: 'text-emerald-500',
    iconBg: 'from-emerald-500 to-emerald-600',
    soft: 'bg-emerald-50',
  },
  Education: {
    stripe: 'from-amber-400 to-amber-600',
    chip: 'bg-amber-100 text-amber-700',
    ring: 'text-amber-500',
    iconBg: 'from-amber-500 to-amber-600',
    soft: 'bg-amber-50',
  },
  Progress: {
    stripe: 'from-cyan-400 to-cyan-600',
    chip: 'bg-cyan-100 text-cyan-700',
    ring: 'text-cyan-500',
    iconBg: 'from-cyan-500 to-cyan-600',
    soft: 'bg-cyan-50',
  },
  Collaboration: {
    stripe: 'from-pink-400 to-pink-600',
    chip: 'bg-pink-100 text-pink-700',
    ring: 'text-pink-500',
    iconBg: 'from-pink-500 to-pink-600',
    soft: 'bg-pink-50',
  },
};

const themeFor = (category: string): CategoryTheme =>
  CATEGORY_THEME[category as CategoryName] ?? CATEGORY_THEME.Communication;

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 5) return 'Burning the midnight oil';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Working late';
};

// Easing-based count-up so stat values animate in on mount.
const useAnimatedNumber = (target: number, durationMs = 900) => {
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
};

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: number;
  format?: (n: number) => string;
  tint: string;
  glow?: string;
  pulse?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, format, tint, glow, pulse }) => {
  const animated = useAnimatedNumber(value);
  const display = format ? format(animated) : Math.round(animated).toString();
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl bg-white border border-[#ECECEC] p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ${
        glow ?? ''
      }`}
    >
      <div
        className={`absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br ${tint} opacity-15 blur-2xl group-hover:opacity-25 transition-opacity`}
      />
      <div className="relative z-10 flex items-start justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-[#777]">{label}</div>
          <div className="mt-2 text-3xl font-bold font-poppins text-[#1A1A1A] tabular-nums">{display}</div>
        </div>
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${tint} text-white shadow-md ${pulse ? 'animate-pulse' : ''}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

interface ScoreRingProps {
  score: number;
  max?: number;
  toneClass: string;
}

const ScoreRing: React.FC<ScoreRingProps> = ({ score, max = 5, toneClass }) => {
  const pct = Math.max(0, Math.min(1, score / max));
  const r = 22;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-14 h-14 flex-shrink-0">
      <svg viewBox="0 0 56 56" className="w-14 h-14 -rotate-90">
        <circle cx="28" cy="28" r={r} stroke="currentColor" strokeOpacity="0.12" strokeWidth="5" fill="none" className={toneClass} />
        <circle
          cx="28"
          cy="28"
          r={r}
          stroke="currentColor"
          strokeWidth="5"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          className={toneClass}
          style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-[#1A1A1A] tabular-nums">{score}</span>
        <span className="text-[10px] text-[#888] ml-0.5">/{max}</span>
      </div>
    </div>
  );
};

const KipCardSkeleton: React.FC = () => (
  <div className="relative overflow-hidden bg-white p-4 sm:p-6 rounded-2xl border border-[#ECECEC]">
    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#ECECEC]" />
    <div className="flex items-center gap-4 animate-pulse">
      <div className="w-12 h-12 rounded-full bg-[#ECECEC]" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-[#ECECEC] rounded w-2/3" />
        <div className="h-3 bg-[#ECECEC] rounded w-1/3" />
      </div>
      <div className="w-14 h-14 rounded-full bg-[#ECECEC]" />
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [showAllKips, setShowAllKips] = React.useState(false);
  const { profile, kips, loading, error, isAdmin, clients, colleagues } = useKiprData();

  const playable = React.useCallback(
    (k: Kip) => isLearnerKipPlayable(k, clients, colleagues, isAdmin),
    [clients, colleagues, isAdmin],
  );

  const recentKips = kips.filter((kip) => kip.completed).slice(0, 3);
  const allCompletedKips = kips.filter((kip) => kip.completed);
  const totalCompleted = allCompletedKips.length;

  const nextKip = React.useMemo(() => {
    const open = kips.find((k) => !k.completed && playable(k));
    if (open) return open;
    const replay = kips.find((k) => k.completed && playable(k));
    if (replay) return replay;
    return kips.find(playable) ?? null;
  }, [kips, playable]);
  const allCaughtUp = nextKip ? nextKip.completed === true : false;

  // Group kips by category for the coverage card.
  const categoryStats = React.useMemo(() => {
    const groups = new Map<string, { total: number; done: number }>();
    kips.forEach((k) => {
      const g = groups.get(k.category) ?? { total: 0, done: 0 };
      g.total += 1;
      if (k.completed) g.done += 1;
      groups.set(k.category, g);
    });
    return Array.from(groups.entries())
      .map(([name, g]) => ({ name, ...g, pct: g.total ? g.done / g.total : 0 }))
      .sort((a, b) => b.total - a.total);
  }, [kips]);

  const renderStars = (score: number) =>
    Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={14}
        className={i < score ? 'fill-yellow-400 text-yellow-400' : 'text-[#DADADA]'}
      />
    ));

  const skills = [
    { name: 'Communication', value: profile.skills.communication, color: 'from-blue-500 to-blue-600' },
    { name: 'Accuracy', value: profile.skills.accuracy, color: 'from-emerald-500 to-emerald-600' },
    { name: 'Empathy', value: profile.skills.empathy, color: 'from-purple-500 to-purple-600' },
  ];

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Hero Header with Particles */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#121212] via-[#1A1A1A] to-[#2A2A2A] rounded-3xl p-12 text-white">
        <Particles
          className="absolute inset-0"
          quantity={80}
          ease={70}
          color="#ffffff"
          size={0.6}
          staticity={40}
        />
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center mb-4">
            <h1 className="text-5xl font-bold font-poppins bg-gradient-to-r from-white via-blue-100 to-blue-200 bg-clip-text text-transparent">
              {getGreeting()}, {profile.name}
            </h1>
          </div>
          <p className="text-xl text-blue-100 font-inter mb-8 max-w-2xl mx-auto">
            Master therapist skills through immersive AI-powered training scenarios
          </p>

          {/* Level Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
              <Trophy className="w-6 h-6 text-yellow-400" />
              <span className="text-lg font-bold">Level {profile.level}</span>
            </div>
          </div>

          {/* Primary CTA */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/personas')}
              className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white px-12 py-5 rounded-full shadow-2xl font-bold font-inter text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-purple-500/30 flex items-center space-x-3"
            >
              <span>Start training</span>
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip - tucks slightly under the hero for visual cohesion */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-1 -mt-4 relative z-20">
        <StatCard
          icon={Flame}
          label="Day streak"
          value={profile.stats.kipStreak}
          tint="from-orange-500 to-red-500"
          pulse={profile.stats.kipStreak > 0}
        />
        <StatCard
          icon={Star}
          label="Avg score"
          value={profile.stats.avgScore}
          format={(n) => n.toFixed(1)}
          tint="from-yellow-400 to-amber-500"
        />
        <StatCard
          icon={Target}
          label="This week"
          value={profile.stats.completedThisWeek}
          tint="from-emerald-500 to-teal-500"
        />
        <StatCard
          icon={Layers}
          label="Total Kips"
          value={totalCompleted}
          tint="from-blue-500 to-purple-500"
        />
      </div>

      {/* Continue training + Skills */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Continue Training (col-span-2) */}
        <div className="lg:col-span-2">
          {nextKip ? (
            (() => {
              const theme = themeFor(nextKip.category);
              const personaName = nextKip.client?.name ?? 'AI Persona';
              const personaAvatar = nextKip.client?.avatar;
              return (
                <button
                  onClick={() => playable(nextKip) && navigate(`/scenario/${nextKip.id}`)}
                  disabled={!playable(nextKip)}
                  className={`group relative overflow-hidden w-full text-left rounded-3xl bg-white border border-[#ECECEC] shadow-sm transition-all duration-300 ${
                    playable(nextKip)
                      ? 'hover:shadow-xl hover:-translate-y-0.5'
                      : 'opacity-60 cursor-not-allowed'
                  }`}
                >
                  {/* Decorative gradient blob */}
                  <div className={`absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br ${theme.iconBg} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity`} />

                  <div className="relative z-10 p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-stretch">
                    {/* Persona avatar */}
                    <div className="relative flex-shrink-0 self-center sm:self-start">
                      <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${theme.iconBg} opacity-20 blur-xl`} />
                      {personaAvatar ? (
                        <img
                          src={personaAvatar}
                          alt={personaName}
                          className="relative w-24 h-24 rounded-3xl object-cover border-4 border-white shadow-lg"
                        />
                      ) : (
                        <div className={`relative w-24 h-24 rounded-3xl bg-gradient-to-br ${theme.iconBg} flex items-center justify-center shadow-lg`}>
                          <Sparkles className="w-10 h-10 text-white" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#777]">
                          {allCaughtUp ? 'Replay your favorite' : 'Continue training'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${theme.chip}`}>
                          {nextKip.category}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold font-poppins text-[#1A1A1A] mb-1 truncate">
                        {nextKip.title}
                      </h3>
                      <p className="text-sm text-[#555] line-clamp-2 mb-4">{nextKip.description}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#777]">
                        <span className="flex items-center gap-1.5">
                          <Calendar size={14} /> {nextKip.duration}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Compass size={14} /> {personaName}
                        </span>
                      </div>
                    </div>

                    <div className="flex sm:flex-col items-center justify-center sm:justify-end gap-2 flex-shrink-0">
                      <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${theme.iconBg} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}>
                        <Play className="w-6 h-6 ml-0.5 fill-white" />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })()
          ) : (
            <div className="rounded-3xl bg-white border border-[#ECECEC] p-8 text-center text-[#777]">
              {loading ? 'Loading your next training...' : 'No training scenarios available yet.'}
            </div>
          )}
        </div>

        {/* Skills (col-span-1) */}
        <div className="rounded-3xl bg-white border border-[#ECECEC] p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold font-poppins text-[#1A1A1A]">Your skills</h3>
            <button
              onClick={() => navigate('/profile')}
              className="text-xs text-[#777] hover:text-[#1A1A1A] transition-colors"
            >
              View profile →
            </button>
          </div>
          <div className="space-y-4">
            {skills.map((skill) => (
              <div key={skill.name}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-sm font-medium text-[#1A1A1A]">{skill.name}</span>
                  <span className="text-xs text-[#777] tabular-nums">{skill.value}%</span>
                </div>
                <div className="w-full bg-[#F1F1F1] rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 rounded-full bg-gradient-to-r ${skill.color} transition-all duration-700 ease-out`}
                    style={{ width: `${skill.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Kips + Category coverage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Kips (col-span-2) */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold font-poppins text-[#1A1A1A]">
              {showAllKips ? 'All Completed Kips' : 'Recent Kips'}
            </h2>
            {totalCompleted > 0 && (
              <span className="text-sm text-[#777]">
                {totalCompleted} completed
              </span>
            )}
          </div>

          <div className="space-y-3">
            {loading && recentKips.length === 0 && (
              <>
                <KipCardSkeleton />
                <KipCardSkeleton />
                <KipCardSkeleton />
              </>
            )}

            {(showAllKips ? allCompletedKips : recentKips).map((kip, i) => {
              const theme = themeFor(kip.category);
              const personaAvatar = kip.client?.avatar;
              const personaName = kip.client?.name ?? 'AI Persona';
              const score = kip.score ?? 0;

              return (
                <div
                  key={kip.id}
                  className="group relative overflow-hidden bg-white p-4 sm:p-5 rounded-2xl border border-[#ECECEC] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                  style={{ animation: `fadeSlideUp 400ms ease-out ${i * 60}ms both` }}
                >
                  {/* Category color stripe */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${theme.stripe}`} />

                  <div className="pl-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Persona avatar */}
                      {personaAvatar ? (
                        <img
                          src={personaAvatar}
                          alt={personaName}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0"
                        />
                      ) : (
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${theme.iconBg} text-white shadow-sm`}>
                          <Sparkles className="w-5 h-5" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold font-inter text-[#1A1A1A] truncate">{kip.title}</h3>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs text-[#777]">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {kip.date}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full font-medium ${theme.chip}`}>
                            {kip.category}
                          </span>
                          <span className="hidden sm:flex items-center gap-0.5">{renderStars(score)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                      <ScoreRing score={score} toneClass={theme.ring} />
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => playable(kip) && navigate(`/scenario/${kip.id}`)}
                          disabled={!playable(kip)}
                          className={`p-2 rounded-lg transition-colors ${
                            playable(kip)
                              ? 'text-[#777] hover:text-[#1A1A1A] hover:bg-[#F5F5F5]'
                              : 'text-[#CCC] cursor-not-allowed'
                          }`}
                          title="Replay"
                          aria-label="Replay"
                        >
                          <RotateCcw size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/report/${kip.id}`)}
                          className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors px-3 py-2 rounded-lg hover:bg-blue-50"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {!loading && (showAllKips ? allCompletedKips : recentKips).length === 0 && (
              <div className="rounded-2xl border border-dashed border-[#DADADA] bg-white p-10 text-center">
                <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white">
                  <Sparkles className="w-6 h-6" />
                </div>
                <p className="text-[#555] mb-4">No completed Kips yet — your first session is one click away.</p>
                <button
                  onClick={() => navigate('/personas')}
                  className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-black transition-colors"
                >
                  Start your first Kip <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>

          {totalCompleted > 3 && (
            <div className="text-center mt-4">
              <button
                onClick={() => setShowAllKips(!showAllKips)}
                className="inline-flex items-center justify-center space-x-2 text-[#555555] hover:text-[#1A1A1A] transition-colors px-4 py-2 rounded-lg hover:bg-[#F5F5F5]"
              >
                <span className="text-sm font-medium">
                  {showAllKips ? 'Show recent only' : `View all ${totalCompleted} completed Kips`}
                </span>
                {showAllKips ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          )}
        </div>

        {/* Category coverage (col-span-1) */}
        <div className="rounded-3xl bg-white border border-[#ECECEC] p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold font-poppins text-[#1A1A1A]">Category coverage</h3>
          </div>

          {categoryStats.length === 0 ? (
            <div className="text-sm text-[#777]">
              {loading ? 'Loading categories...' : 'No categories yet.'}
            </div>
          ) : (
            <div className="space-y-3">
              {categoryStats.map((cat) => {
                const theme = themeFor(cat.name);
                return (
                  <div key={cat.name} className={`relative overflow-hidden rounded-xl ${theme.soft} p-3`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${theme.chip}`}>
                        {cat.name}
                      </span>
                      <span className="text-xs text-[#555] tabular-nums">
                        {cat.done}/{cat.total}
                      </span>
                    </div>
                    <div className="w-full bg-white/60 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full bg-gradient-to-r ${theme.iconBg} transition-all duration-700 ease-out`}
                        style={{ width: `${cat.pct * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Lightweight keyframes for staggered card entrance.
          Kept inline so the dashboard is fully self-contained. */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
